import React, { useState, useContext, useRef } from 'react';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, setDoc, serverTimestamp, collection, query, where, limit, getDocs, orderBy, updateDoc, arrayUnion } from 'firebase/firestore';
import { AppContext } from './AppContext';
import { firestore } from './firebase';
import { ProfileComponentShareProfile } from './ProfileComponentShareProfile';
import { ProfileComponentMyProfile } from './ProfileComponentMyProfile';
import { useNavigate } from 'react-router-dom';

const PostScreenShareDATprofile = ({ onNavigate, onPost, viewingProfile }) => {
  const [text, setText] = useState('');
  const [uploading, setUploading] = useState(false);

    const navigate = useNavigate();

  const {
    currentUser,
    posts,
    setPosts,
    userId2,
  } = useContext(AppContext);

  const handlePost = async () => {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      alert('Please log in to post');
      return;
    }
  
    console.log("handlePost triggered");
    setUploading(true);
  
    try {
      console.log("Starting tag search...");
      // 1) Resolve tags
      const atPattern = /@(\w+)/g;
      let usernames = [...text.matchAll(atPattern)].map(match => match[1]);
      let localTags = [];
      let updatedText = text;
  
      if (usernames.length > 0) {
        const usersRef = collection(firestore, 'users');
        for (const username of usernames) {
          console.log(`Searching for user: ${username}`);
          const q = query(
            usersRef,
            where('username_lower', '==', username.toLowerCase()),
            limit(1)
          );
          const snap = await getDocs(q);
          if (!snap.empty) {
            const docSnap = snap.docs[0];
            const u = docSnap.data();
            console.log(`User found: ${u.username} (ID: ${docSnap.id})`);
  
            const startIdx = updatedText.indexOf(`@${username}`);
            const endIdx = startIdx + u.username.length + 1;
            localTags.push({
              username: u.username,
              userId: docSnap.id,
              start: startIdx,
              end: endIdx
            });
  
            updatedText =
              updatedText.slice(0, startIdx) +
              `@${u.username}` +
              updatedText.slice(startIdx + username.length + 1);
          } else {
            console.log(`No matching user found for: ${username}`);
          }
        }
      }
  
      console.log("Final tags to be used:", localTags);
      console.log("Updated text with correct capitalization:", updatedText);
  
      // 2) Build postData
      const db = getFirestore();
      const docRef = doc(collection(db, 'posts'));
      const postId = docRef.id;
  
      const userRef = userId2 ? doc(db, 'users', userId2) : null;
      const artistUserRef = viewingProfile?.userId
        ? doc(db, 'users', viewingProfile.userId)
        : null;
  
      const profile = {
        userId: viewingProfile?.userId,
        name: viewingProfile?.artistName || viewingProfile?.ArtistName,
        profileImageUrl: viewingProfile.profileImageUrl,
        artistUserRef,
        accountType: viewingProfile.accountType,
        username: viewingProfile?.username
      };
  
      const postData = {
        id: postId,
        postId,
        text: updatedText,
        userRef,
        accountType: currentUser.accountType,
        userId: user.uid,
        username: currentUser.username,
        name: currentUser.name,
        profileImageUrl: currentUser.profileImageUrl,
        createdAt: serverTimestamp(),
        likeCount: 0,
        repostCount: 0,
        likes: {},
        comments: [],
        repostUserIds: [],
        profile,
        tags: localTags
      };
  
      // 3) Write to 'posts'
      await setDoc(docRef, postData);
      console.log("Post created with ID:", postId);
  
      // 4) Update local state
      setPosts(prev => [...prev, postData]);
  
      // 5) Mirror client‐side socialSummaries logic
      // Use a real timestamp for summaries entries
      const summaryEntry = { 
        ...postData, 
        createdAt: Date.now() 
      };
      const overhead = 200;
      const estimatedSize =
        new Blob([JSON.stringify(summaryEntry)]).size + overhead;
  
      const summariesCol = collection(db, 'socialSummaries');
      const latestQ = query(
        summariesCol,
        orderBy('__name__', 'desc'),
        limit(1)
      );
      const latestSnap = await getDocs(latestQ);
  
      if (!latestSnap.empty) {
        const latestDoc = latestSnap.docs[0];
        const { sizeEstimate = 0 } = latestDoc.data();
  
        if (sizeEstimate + estimatedSize > 950000) {
          const idx = parseInt(latestDoc.id.split('_')[1], 10) + 1;
          const newRef = doc(summariesCol, `summary_${idx}`);
          await setDoc(newRef, {
            posts: [summaryEntry],
            sizeEstimate: estimatedSize
          });
        } else {
          await updateDoc(latestDoc.ref, {
            posts: arrayUnion(summaryEntry),
            sizeEstimate: sizeEstimate + estimatedSize
          });
        }
      } else {
        const firstRef = doc(summariesCol, 'summary_1');
        await setDoc(firstRef, {
          posts: [summaryEntry],
          sizeEstimate: estimatedSize
        });
      }
  
      // 6) Navigate & reset
      navigate('/social');
      setText('');


  
    } catch (error) {
      console.error('Error creating post:', error);
      alert('Error creating post. Please try again later.');
    } finally {
      setUploading(false);
    }
  };

  // Styles object
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: '7vh',
      width: '100%',
    },
    commentSectionContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
    postContainer: {
      padding: '0.7vh',
      backgroundColor: 'rgba(0,0,0,0.5)',
      borderRadius: '8px',
      border: '1px solid rgba(23, 195, 250, 0.5)',
      marginLeft: '10px',
      marginRight: '10px',
      marginTop: '2vh',
    },
    addCommentContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%',
    },
    textInput: {
      padding: '1.5vh 4vw',
      width: '80%',
      fontSize: '20px',
      color: 'white',
      border: 'none',
      resize: 'none',
      zIndex: 100,
      marginBottom: '10px',
      textAlign: 'left',
      borderRadius: '20px',
      outline: 'none',
      backgroundColor: 'transparent',
    },
    characterCount: {
      color: 'white',
      textAlign: 'right',
      padding: '10px',
      fontSize: '13.5px',
      width: '80%',
      alignSelf: 'center',
    },
    buttonsRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'center', // Center the buttons horizontally
      alignItems: 'center',     // Align items vertically at the center
      width: '100%',

    },
    buttonContainer: {
      display: 'flex',
      flexDirection: 'column', // Stack the button and text vertically
      alignItems: 'center',
      margin: '0 10px',
    },
    button: {
      padding: '10px 20px',
      color: '#62fbfa',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      border: 'none',
      fontSize: '20px',
      marginBottom: '5px',
      zIndex: 100,
      cursor: 'pointer',
    },
    limitText: {
      color: 'white',
      fontSize: '13.5px',
      textAlign: 'center',
    },
    fileInput: {
      display: 'none',
    },
    previewContainer: {
      cursor: 'pointer',
      marginBottom: '10px',
      textAlign: 'center',
    },
    mediaPreview: {
      width: '50vw',
      height: '50vw',
      marginBottom: '10px',
      borderRadius: '10px',
      objectFit: 'contain',
    },
    progressBarContainer: {
      width: '80%',
      backgroundColor: '#ccc',
      marginTop: '10px',
      height: '10px',
      alignSelf: 'center',
    },
    progressBar: {
      height: '100%',
      backgroundColor: '#6200EE',
    },
    fullScreenContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      zIndex: 1000,
    },
    fullScreenMedia: {
      maxWidth: '100%',
      maxHeight: '100%',
      objectFit: 'contain',
    },
    modal: {
      display: 'block',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.commentSectionContainer}>
        <div style={styles.postContainer}>
          <ProfileComponentMyProfile />

          <div style={styles.addCommentContainer}>
            <textarea
              placeholder="What's happening?"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={1000}
              style={styles.textInput}
            />
            <div style={styles.characterCount}>{text.length}/1000</div>
          </div>

          <ProfileComponentShareProfile userProfile={viewingProfile} />
        </div>
      </div>

      {uploading ? (
        <div style={styles.buttonContainer}>
          <div className="loader">Loading...</div>
        </div>
      ) : (
        <div style={styles.buttonsRow}>
          <button
            style={styles.button}
            onClick={handlePost}
            disabled={uploading}
          >
            POST DAT
          </button>
        </div>
      )}
    </div>
  );
};

export { PostScreenShareDATprofile };
